import React, { useState, useRef, useEffect } from 'react';
import {
 IconArrowLeft,
 IconLock,
 IconExternalLink,
 IconUsers,
 IconSend,
 IconClock,
 IconCash,
 IconPlus,
 IconX,
 IconEye,
 IconRocket,
 IconSearch
} from '@tabler/icons-react';
import GoLiveModal from './GoLiveModal';
import InlineEdit from '../../../Operations/components/InlineEdit';
import { getCreatorDisplayName, getCreatorInitials } from '../../../../../utils/creatorHelpers';

const statusDisplay = (deliveryStatus, hasTweet) => {
 const status = getCreatorStatusKey({ deliveryStatus, tweet: hasTweet ? { url: true } : null });
 if (status === 'paid') {
 return { label: 'Paid', className: 'bg-accent text-white' };
 }
 if (status === 'posted') {
 return { label: 'Posted', className: 'bg-accent-muted text-accent' };
 }
 return { label: 'Waiting', className: 'bg-surface-2 text-text-primary' };
};

const getCreatorStatusKey = (creator) => {
 if (creator.deliveryStatus === 'PAID') return 'paid';
 if (creator.deliveryStatus === 'POSTED' || creator.tweet?.url) return 'posted';
 return 'waiting';
};

const ManageGroupView = ({
 group,
 campaign,
 discordGuildId,
 creators,
 activating = false,
 onBack,
 onSubmitPost,
 onGoLive,
 onManualImpressions
}) => {
 const groupCreators = creators.filter(c => c.groupId === group._id);
 const assignedCount = groupCreators.length;
 const [searchQuery, setSearchQuery] = useState('');
 const [statusFilter, setStatusFilter] = useState('all');
 const paidCount = groupCreators.filter(c => c.deliveryStatus === 'PAID').length;
 const postedCount = groupCreators.filter(c =>
 c.deliveryStatus !== 'PAID' && (c.deliveryStatus === 'POSTED' || c.tweet?.url)
 ).length;
 const waitingCount = assignedCount - postedCount - paidCount;
 const matchesSearch = (creator) => {
 if (!searchQuery.trim()) return true;
 const q = searchQuery.toLowerCase().trim();
 return (
 (creator.name && creator.name.toLowerCase().includes(q)) ||
 (creator.twitterHandle && creator.twitterHandle.toLowerCase().includes(q))
 );
 };
 const filteredCreators = groupCreators.filter(creator =>
 (statusFilter === 'all' || getCreatorStatusKey(creator) === statusFilter) &&
 matchesSearch(creator)
 );
 const activeFilterLabel = {
 all: 'Assigned',
 waiting: 'Waiting',
 posted: 'Posted',
 paid: 'Paid'
 }[statusFilter];

 const messageSent = !!group.goLiveMessageSentAt;
 const canGoLive = !messageSent;
 const [showGoLive, setShowGoLive] = useState(false);

 // Manual content submission state
 const [addingPostForCreatorId, setAddingPostForCreatorId] = useState(null);
 const [newPostUrl, setNewPostUrl] = useState('');
 const postInputRef = useRef(null);

 useEffect(() => {
 if (addingPostForCreatorId && postInputRef.current) {
 postInputRef.current.focus();
 }
 }, [addingPostForCreatorId]);

 const handleOpenAddPost = (creatorId) => {
 setAddingPostForCreatorId(creatorId);
 setNewPostUrl('');
 };

 const handleSubmitPost = () => {
 if (!newPostUrl.trim()) return;
 onSubmitPost(addingPostForCreatorId, newPostUrl.trim(), group._id);
 setAddingPostForCreatorId(null);
 setNewPostUrl('');
 };

 const renderStatCard = ({ key, label, value, icon: Icon, iconClassName }) => {
 const isActive = statusFilter === key;
 return (
 <button
 type="button"
 onClick={() => setStatusFilter(key)}
 aria-pressed={isActive}
 className={`bg-surface-1 border p-3 sm:p-6 text-left transition-colors ${
 isActive
 ? 'border-accent bg-accent-muted/20'
 : 'border-glass-border hover:border-accent/60 hover:bg-glass-bg-hover'
 }`}
 >
 <h3 className="text-sm sm:text-lg font-semibold mb-2 sm:mb-4 text-text-primary">{label}</h3>
 <div className="flex items-center">
 <Icon className={`h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 ${iconClassName}`} />
 <p className="text-xl sm:text-2xl font-bold text-text-primary">
 {value}
 </p>
 </div>
 </button>
 );
 };

 const discordResourceId = group.discord?.channelId || group.discord?.threadId ||
 (group.discord?.mode === 'shared' ? campaign.discordChannel?.id : null);
 const discordUrl = discordGuildId && discordResourceId
 ? `https://discord.com/channels/${discordGuildId}/${discordResourceId}`
 : null;

 return (
 <div>
 {/* Header */}
 <div className="bg-surface-1 border border-glass-border p-4 sm:p-6 mb-6">
 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
 <div className="min-w-0">
 <button
 onClick={onBack}
 aria-label="Back to campaign overview"
 className="flex items-center gap-2 mb-3 text-sm text-text-secondary hover:text-accent min-h-[36px]"
 >
 <IconArrowLeft size={18} />
 Back to Campaign
 </button>

 <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
 <h1 className="text-xl sm:text-2xl font-bold text-text-primary">
 {group.name}
 </h1>
 {group.status === 'active' ? (
 <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium bg-accent-muted text-accent border border-accent/30">
 <IconLock size={12} />
 Active
 </span>
 ) : group.status === 'completed' ? (
 <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-accent text-white">
 Completed
 </span>
 ) : (
 <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-surface-2 text-text-secondary">
 Draft
 </span>
 )}
 </div>

 <p className="text-sm text-text-secondary">
 {campaign.name} &bull; {assignedCount}/{group.groupSize} creators &bull; $60/creator
 </p>
 </div>

 <div className="flex-shrink-0 flex flex-wrap gap-2">
 {canGoLive && (
 <button
 onClick={() => setShowGoLive(true)}
 disabled={activating || assignedCount === 0}
 title={assignedCount === 0 ? 'Assign creators first' : undefined}
 className={`w-full sm:w-auto px-5 py-2 text-white flex items-center justify-center gap-2 text-sm font-medium min-h-[44px] ${
 activating || assignedCount === 0
 ? 'bg-accent/60 cursor-not-allowed'
 : 'bg-accent hover:bg-accent-hover'
 }`}
 >
 <IconRocket size={18} className={activating ? 'animate-pulse' : ''} />
 {activating ? 'Going Live...' : group.status === 'active' ? 'Post Brief' : 'Go Live'}
 </button>
 )}
 {group.status === 'active' && discordUrl && (
 <button
 onClick={() => window.open(discordUrl, '_blank', 'noopener,noreferrer')}
 className="w-full sm:w-auto px-5 py-2 border border-glass-border flex items-center justify-center gap-2 text-sm font-medium text-text-primary hover:bg-glass-bg-hover min-h-[44px]"
 >
 <IconExternalLink size={18} />
 Open in Discord
 </button>
 )}
 </div>
 </div>
 </div>

 {/* Stats Row */}
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-6">
 {renderStatCard({
 key: 'all',
 label: 'Assigned',
 value: `${assignedCount}/${group.groupSize}`,
 icon: IconUsers,
 iconClassName: 'text-accent'
 })}
 {renderStatCard({
 key: 'waiting',
 label: 'Waiting',
 value: waitingCount,
 icon: IconClock,
 iconClassName: 'text-text-tertiary'
 })}
 {renderStatCard({
 key: 'posted',
 label: 'Posted',
 value: postedCount,
 icon: IconSend,
 iconClassName: 'text-warning'
 })}
 {renderStatCard({
 key: 'paid',
 label: 'Paid',
 value: paidCount,
 icon: IconCash,
 iconClassName: 'text-accent'
 })}
 </div>

 {/* Content Table */}
 <div className="bg-surface-1 border border-glass-border overflow-hidden mb-6">
 <div className="px-3 sm:px-6 py-4 border-b border-glass-border space-y-3">
 <h2 className="text-lg font-semibold text-text-primary">
 Content ({filteredCreators.length})
 </h2>
 {statusFilter !== 'all' && (
 <div className="text-xs text-text-secondary">
 Showing {activeFilterLabel.toLowerCase()} creators. Click Assigned to show all.
 </div>
 )}
 <div className="relative">
 <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
 <input
 type="text"
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 placeholder="Search creators..."
 className="pl-9 pr-8 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-accent focus:border-transparent w-full sm:w-56"
 />
 {searchQuery && (
 <button
 onClick={() => setSearchQuery('')}
 className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
 >
 <IconX size={14} />
 </button>
 )}
 </div>
 </div>

 {/* Mobile card view */}
 <div className="sm:hidden divide-y divide-glass-border">
 {filteredCreators.map(creator => {
 const status = statusDisplay(creator.deliveryStatus, !!creator.tweet?.url);
 return (
 <div key={creator._id} className="p-3">
 <div className="flex items-start gap-3">
 {creator.profileImage ? (
 <img src={creator.profileImage} alt="" className="w-8 h-8 object-cover flex-shrink-0" />
 ) : (
 <div className="w-8 h-8 bg-accent-muted flex items-center justify-center text-xs font-medium text-accent flex-shrink-0">
 {getCreatorInitials(creator)}
 </div>
 )}
 <div className="flex-1 min-w-0">
 <div className="flex items-center justify-between gap-2">
 <div className="min-w-0">
 <div className="text-sm font-medium text-text-primary truncate">{getCreatorDisplayName(creator)}</div>
 {creator.name && <div className="text-xs text-text-secondary truncate">{creator.name}</div>}
 </div>
 <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium flex-shrink-0 ${status.className}`}>
 {status.label}
 </span>
 </div>
 <div className="flex items-center justify-between gap-2 mt-2">
 <div className="flex items-center gap-2">
 {creator.tweet?.url ? (
 <a href={creator.tweet.url} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:text-accent-hover flex items-center gap-1">
 View <IconExternalLink size={12} />
 </a>
 ) : (
 <span className="text-xs text-text-tertiary">Waiting</span>
 )}
 <span className="text-xs text-text-secondary flex items-center gap-1">
 <IconEye size={12} />
 <InlineEdit
 type="number"
 value={creator.tweet?.metrics?.impressions ?? ''}
 placeholder="Add"
 onSave={(v) => onManualImpressions(creator._id, Number(v))}
 />
 </span>
 </div>
 <button
 onClick={() => handleOpenAddPost(creator._id)}
 className="p-1.5 text-text-tertiary hover:text-accent hover:bg-accent-muted min-h-[32px]"
 >
 <IconPlus size={16} />
 </button>
 </div>
 </div>
 </div>
 {addingPostForCreatorId === creator._id && (
 <div className="mt-2 flex items-center gap-2">
 <input
 ref={postInputRef}
 type="text"
 value={newPostUrl}
 onChange={(e) => setNewPostUrl(e.target.value)}
 onKeyDown={(e) => e.key === 'Enter' && handleSubmitPost()}
 placeholder="Paste tweet URL..."
 className="flex-1 px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 <button onClick={handleSubmitPost} disabled={!newPostUrl.trim()} className="px-3 py-2 text-sm font-medium bg-accent hover:bg-accent-hover disabled:bg-surface-3 disabled:cursor-not-allowed text-white">
 OK
 </button>
 <button onClick={() => setAddingPostForCreatorId(null)} className="p-2 text-text-tertiary hover:text-text-primary">
 <IconX size={14} />
 </button>
 </div>
 )}
 </div>
 );
 })}
 </div>

 {/* Desktop table view */}
 <div className="hidden sm:block overflow-x-auto">
 <table className="min-w-full divide-y divide-glass-border">
 <thead className="bg-surface-0">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Creator</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Content</th>
 <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Impressions</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Actions</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-glass-border">
 {filteredCreators.map(creator => {
 const status = statusDisplay(creator.deliveryStatus, !!creator.tweet?.url);
 return (
 <React.Fragment key={creator._id}>
 <tr className="hover:bg-glass-bg-hover">
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center">
 {creator.profileImage ? (
 <img
 src={creator.profileImage}
 alt=""
 className="w-9 h-9 mr-3 object-cover"
 />
 ) : (
 <div className="w-9 h-9 bg-accent-muted flex items-center justify-center text-sm font-medium text-accent mr-3">
 {getCreatorInitials(creator)}
 </div>
 )}
 <div>
 <div className="text-sm font-medium text-text-primary">{getCreatorDisplayName(creator)}</div>
 {creator.name && <div className="text-sm text-text-secondary">{creator.name}</div>}
 </div>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 {creator.tweet?.url ? (
 <a
 href={creator.tweet.url}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center gap-2 text-sm text-accent hover:text-accent-hover"
 >
 View Content
 <IconExternalLink size={14} />
 </a>
 ) : (
 <span className="text-sm text-text-secondary">&mdash; waiting &mdash;</span>
 )}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-right">
 <div className="flex items-center justify-end gap-1.5 text-sm text-text-primary">
 <IconEye size={14} className="text-text-tertiary" />
 <InlineEdit
 type="number"
 value={creator.tweet?.metrics?.impressions ?? ''}
 placeholder={creator.tweet?.url ? 'Not synced' : '\u2014'}
 onSave={(v) => onManualImpressions(creator._id, Number(v))}
 />
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${status.className}`}>
 {status.label}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <button
 onClick={() => handleOpenAddPost(creator._id)}
 title={creator.tweet?.url ? 'Override content' : 'Add content'}
 className="p-1.5 text-text-tertiary hover:text-accent hover:bg-accent-muted"
 >
 <IconPlus size={18} />
 </button>
 </td>
 </tr>
 {addingPostForCreatorId === creator._id && (
 <tr className="bg-accent-muted">
 <td colSpan={5} className="px-6 py-3">
 <div className="flex items-center gap-3">
 <input
 ref={postInputRef}
 type="text"
 value={newPostUrl}
 onChange={(e) => setNewPostUrl(e.target.value)}
 onKeyDown={(e) => e.key === 'Enter' && handleSubmitPost()}
 placeholder="Paste tweet URL (e.g. https://x.com/.../status/...)"
 className="flex-1 px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 <button
 onClick={handleSubmitPost}
 disabled={!newPostUrl.trim()}
 className="px-4 py-2 text-sm font-medium bg-accent hover:bg-accent-hover disabled:bg-surface-3 disabled:cursor-not-allowed text-white"
 >
 Submit
 </button>
 <button
 onClick={() => setAddingPostForCreatorId(null)}
 className="p-2 text-text-tertiary hover:text-text-primary"
 >
 <IconX size={16} />
 </button>
 </div>
 </td>
 </tr>
 )}
 </React.Fragment>
 );
 })}
 </tbody>
 </table>
 </div>

 {groupCreators.length === 0 ? (
 <div className="text-center py-12 text-text-secondary">
 No creators assigned to this group yet.
 </div>
 ) : filteredCreators.length === 0 ? (
 <div className="text-center py-12 text-text-secondary">
 {searchQuery.trim()
 ? `No ${statusFilter === 'all' ? '' : `${activeFilterLabel.toLowerCase()} `}creators matching "${searchQuery}"`
 : `No ${activeFilterLabel.toLowerCase()} creators in this group`}
 </div>
 ) : null}
 </div>

 {/* Go Live Modal */}
 {showGoLive && (
 <GoLiveModal
 campaign={campaign}
 group={group}
 creatorCount={assignedCount}
 posting={activating}
 onPost={async ({ briefUrl, message, channelMode }) => {
 const succeeded = await onGoLive(group._id, { briefUrl, message, channelMode });
 if (succeeded) setShowGoLive(false);
 }}
 onClose={() => setShowGoLive(false)}
 />
 )}
 </div>
 );
};

export default ManageGroupView;
